/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Color from 'color';
// material
import { ColorPicker, Icon, Divider, sendErrorMessage } from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionDescription from 'material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription'
import ConfigController from 'material/expandablesection/controllers/configcontroller/configcontroller'
// utils
import { saveRatingsColorConfiguration } from 'state/actions/ratingsState/configStatus';
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/ratings/ratingsmanager/configuration/ratingscolorschemesection/ratingscolorschemesection_styles';
// svg assets
import { ReactComponent as SmallStarFilled } from 'pages/dashboard/ratings/imgassets/smallstar-filled.svg';
import { ReactComponent as SmallStarEmpty } from 'pages/dashboard/ratings/imgassets/smallstar-empty.svg';

const RatingsColorSchemeSection = (props) => {
  // Grab initial saved color configuration from redux state
  const savedColorConfiguration = useSelector(({ ratingsState }) => ratingsState.configStatus.configuration.colorConfiguration);
  const savingColorConfiguration = useSelector(({ ratingsState }) => ratingsState.configStatus.savingColorConfiguration);

  // Dynamically changing internal state
  const [backgroundcolor, setbackgroundcolor] = useState(savedColorConfiguration.background.value);
  const [primarytextcolor, setprimarytextcolor] = useState(savedColorConfiguration.primaryText.value);
  const [brandingcolor, setbrandingcolor] = useState(savedColorConfiguration.brand.value);
  const [errorvalidationcolor, seterrorvalidationcolor] = useState(savedColorConfiguration.errorValidation.value);
  // valid proper
  const [backgroundcolorvalid, setbackgroundcolorvalid] = useState(!savedColorConfiguration.background.valid ? true : savedColorConfiguration.background.valid);
  const [primarytextcolorvalid, setprimarytextcolorvalid] = useState(!savedColorConfiguration.primaryText.valid ? true: savedColorConfiguration.primaryText.valid);
  const [brandingcolorvalid, setbrandingcolorvalid] = useState(!savedColorConfiguration.brand.valid ? true : savedColorConfiguration.brand.valid);
  const [errorvalidationcolorvalid, seterrorvalidationcolorvalid] = useState(!savedColorConfiguration.errorValidation.valid ? true : savedColorConfiguration.errorValidation.valid);

  const [isOpened, setIsOpened] = useState(false);
  const dispatch = useDispatch();

  const processColorUpdate = (color, name) => {
    switch(name) {
      case 'Background':
        if (color === savedColorConfiguration.background.value) setbackgroundcolorvalid(true);
        setbackgroundcolor(color)
        return;
      case 'Primary Text':
        if (color === savedColorConfiguration.primaryText.value) setprimarytextcolorvalid(true);
        setprimarytextcolor(color)
        return;
      case 'Branding':
        if (color === savedColorConfiguration.brand.value) setbrandingcolorvalid(true);
        setbrandingcolor(color)
        return;
      case 'Error Validation':
        if (color === savedColorConfiguration.errorValidation.value) seterrorvalidationcolorvalid(true);
        seterrorvalidationcolor(color)
        return;
    }
  }

  const disableSave = backgroundcolor.toUpperCase() === savedColorConfiguration.background.value.toUpperCase()
    && primarytextcolor.toUpperCase() === savedColorConfiguration.primaryText.value.toUpperCase()
    && brandingcolor.toUpperCase() === savedColorConfiguration.brand.value.toUpperCase()
    && errorvalidationcolor.toUpperCase() === savedColorConfiguration.errorValidation.value.toUpperCase();

  const validateColor = (isValid, name) => {
    switch(name) {
      case 'Background':
        setbackgroundcolorvalid(isValid)
        return;
      case 'Primary Text':
        setprimarytextcolorvalid(isValid)
        return;
      case 'Branding':
        setbrandingcolorvalid(isValid)
        return;
      case 'Error Validation':
        seterrorvalidationcolorvalid(isValid)
        return;
    }
  }

  const collapse = () => {
    setIsOpened(false);
  }

  const handleColorSchemeSave = () => {
    const customColorSchema = {
      background: backgroundcolor,
      primaryText: primarytextcolor,
      brand: brandingcolor,
      errorValidation: errorvalidationcolor,
    };
    const validToSave = allColorsValid();

    if (!disableSave && validToSave) {
      dispatch(saveRatingsColorConfiguration(savedColorConfiguration.uuid, customColorSchema, collapse));
    } else if (!validToSave) {
      dispatch(sendErrorMessage('Cannot save: invalid colors'));
      setIsOpened(true);
    }
  }

  const allColorsValid = () => {
    return backgroundcolorvalid && primarytextcolorvalid && brandingcolorvalid && errorvalidationcolorvalid;
  }

  const handleCancel = () => {
    setbackgroundcolor(savedColorConfiguration.background.value);
    setbackgroundcolorvalid(true);
    setprimarytextcolor(savedColorConfiguration.primaryText.value);
    setprimarytextcolorvalid(true);
    setbrandingcolor(savedColorConfiguration.brand.value);
    setbrandingcolorvalid(true);
    seterrorvalidationcolor(savedColorConfiguration.errorValidation.value);
    seterrorvalidationcolorvalid(true);
  }

  const handleReset = (name) => {
    switch(name) {
      case 'Background':
        setbackgroundcolor(savedColorConfiguration.background.value);
        setbackgroundcolorvalid(true);
        return;
      case 'Primary Text':
        setprimarytextcolor(savedColorConfiguration.primaryText.value);
        setprimarytextcolorvalid(true);
        return;
      case 'Branding':
        setbrandingcolor(savedColorConfiguration.brand.value);
        setbrandingcolorvalid(true);
        return;
    }
  }

  const controller = (
    <ConfigController
      isOpened={isOpened}
      setIsOpened={setIsOpened}
      handleCancel={handleCancel}
      handleSave={handleColorSchemeSave}
      processing={savingColorConfiguration}
      disableSave={disableSave}
      title='Color Scheme'
      description={<SectionDescription
        content={
          <div className={'colorspreview'}>
            <span className={`swatch`} style={{ background: savedColorConfiguration.background.value, right: '-15px', zIndex: 1 }} />
            <span className={`swatch`} style={{ background: savedColorConfiguration.primaryText.value, zIndex: 2 }} />
            <span className={`swatch`} style={{ background: savedColorConfiguration.brand.value, left: '-15px', zIndex: 3 }} />
          </div>
        }
      />}
    />
  );

  const colortorgba = (colorstring, opacity) => {
    try {
      const cl = Color(colorstring);
      return cl.alpha(opacity).hsl().string();
    } catch(err) {
      return colorstring;
    }
  }

  const demoRatings = [
    {starIndex: 5, percentage: '80%', count: 108, width: 78.57},
    {starIndex: 4, percentage: '8%', count: 10, width: 17.53},
    {starIndex: 3, percentage: '8%', count: 10, width: 17.53},
    {starIndex: 2, percentage: '2%', count: 3, width: 5.51},
    {starIndex: 1, percentage: '2%', count: 3, width: 5.51}
  ];

  return <ExpandableSection
    className={`ratingscolorschemesection ${props.classes.ratingscolorschemesection}`}
    controller={controller}
    isOpened={isOpened}
    setIsOpened={setIsOpened}
    handleCancel={handleCancel}
    handleSave={handleColorSchemeSave}
    processing={savingColorConfiguration}
    disableSave={disableSave}
  >
    <div className={'colorschemeconfig'}>
      <SectionCard>
        <div className={'content'}>
          <SectionGroup>
            <SectionGroupDescription
              title='Set custom colors'
              description='Customize your color scheme if you want to add personality to the comments.'
            />
            <div className={'colorpickercolumn'}>
              <ColorPicker
                handleReset={() => handleReset('Background')}
                valid={backgroundcolorvalid}
                color={backgroundcolor}
                savedColor={savedColorConfiguration.background.value}
                label={'Background'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'Background')}
                validateColor={(isValid, e) => validateColor(isValid, 'Background')}
              />
              <ColorPicker
                handleReset={() => handleReset('Primary Text')}
                valid={primarytextcolorvalid}
                color={primarytextcolor}
                savedColor={savedColorConfiguration.primaryText.value}
                label={'Primary Text'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'Primary Text')}
                validateColor={(isValid, e) => validateColor(isValid, 'Primary Text')}
              />
              <ColorPicker
                handleReset={() => handleReset('Branding')}
                valid={brandingcolorvalid}
                color={brandingcolor}
                savedColor={savedColorConfiguration.brand.value}
                label={'Branding'}
                processColorUpdate={(color, e) => processColorUpdate(color, 'Branding')}
                validateColor={(isValid, e) => validateColor(isValid, 'Branding')}
              />
            </div>
          </SectionGroup>
          {/*<SectionGroup>*/}
          {/*  <SectionGroupDescription*/}
          {/*    title='Advanced color configuration'*/}
          {/*    description='Configure additional colors if you see any color conflict in the preview mode.'*/}
          {/*  />*/}
          {/*  <div className={'colorpickercolumn'}>*/}
          {/*    <ColorPicker*/}
          {/*      color={errorvalidationcolor}*/}
          {/*      savedColor={savedColorConfiguration.errorValidation.value}*/}
          {/*      label={'Error Validation'}*/}
          {/*      processColorUpdate={(color, e) => processColorUpdate(color, 'Error Validation')}*/}
          {/*      validateColor={(isValid, e) => validateColor(isValid, 'Error Validation')}*/}
          {/*    />*/}
          {/*  </div>*/}
          {/*</SectionGroup>*/}
        </div>
      </SectionCard>
    </div>

    <div className={'previewratingsunit'}>
      <SectionCard>
        <SectionGroup klass={'topsection'}>
          <SectionGroupDescription
            title='Ratings Unit'
            description='The main unit displayed in your site.'
          />
          <div className={'ratingsunit'}>
            <div className={'ratingsunitheader'}>
              <span style={{color: primarytextcolor}}>Thanks for your rating! </span>
              <span style={{color: colortorgba(primarytextcolor, 0.5), fontWeight: 'normal'}}>(134 ratings)</span>
            </div>
            <div className={'starset'} style={{color: brandingcolor}}>
              {demoRatings.slice(0).reverse().map((rating,i) =>
                <div className={'star'} key={i}>
                  <Icon name={'star'} style={i === 3 || i === 4 ? 'far' : ''}/>
                  <span style={{color: colortorgba(primarytextcolor, 0.5)}}>{rating.percentage}</span>
                </div>
              )}
            </div>
          </div>
        </SectionGroup>
          <Divider />
          <SectionGroup>
            <SectionGroupDescription
              title='Ratings Counter'
              description='Activated separately in the Ratings Add-ons section. Requires separate embed code.'
            />
            <div className={'ratingscounter'}>
              <div className={'topbar'}>
                <div className={'ratingscore'} style={{color: primarytextcolor}}>4.6</div>
                <div className={'stars'} style={{color: brandingcolor}}>
                  <SmallStarFilled fill={brandingcolor}/>
                  <SmallStarFilled fill={brandingcolor}/>
                  <SmallStarFilled fill={brandingcolor}/>
                  <SmallStarFilled fill={brandingcolor}/>
                  <SmallStarEmpty stroke={brandingcolor}/>
                </div>
                <div className={'ratingscount'} style={{color: colortorgba(primarytextcolor, 0.75)}}>
                  <span style={{color: colortorgba(primarytextcolor, 0.5)}}>(134) </span>
                  <Icon name={'angle-down'} />
                </div>
              </div>
              <div className={'ratingscounterbox'} style={{boxShadow: `0px 2px 9px 0px ${colortorgba(primarytextcolor, 0.3)}`, background: backgroundcolor}}>
                {demoRatings.map((rating, i) =>
                  <div className={'ratingrow'} key={i} style={{color: primarytextcolor}}>
                    <span style={{width: '15px'}}>{rating.starIndex}</span>
                    <div className={'ratingbar'} style={{background: colortorgba(primarytextcolor, 0.1)}}>
                      <div className={'ratingpercent'} style={{background: brandingcolor, width: `${rating.width}%`}}></div>
                    </div>
                    <span style={{marginRight: '4px'}}>{rating.percentage}</span>
                    <span style={{color: colortorgba(primarytextcolor, 0.5)}}>{`(${rating.count})`}</span>
                  </div>
                )}
                <div className={'divider'} style={{backgroundColor: colortorgba(primarytextcolor, 0.1)}}/>
                <div className={'leaverating'} style={{color: primarytextcolor}}>Leave a rating</div>
              </div>
            </div>
          </SectionGroup>
      </SectionCard>
    </div>
  </ExpandableSection>
}

export default injectSheet(styles)(RatingsColorSchemeSection)
